import { useLocale } from "next-intl";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { VirtualSportQueryKeys } from "./virtualSportQueryKeys";
import {
  DEFAULT_VIRTUAL_SPORT_TYPE,
  getVirtualSportEvents,
  VirtualSportType,
} from "@/api/virtualSport";

import { FeedType, SportEvent } from "@/types/SportsApiResponses";

interface Options {
  feedType: FeedType;
  // Which virtual sport to list; defaults to virtual football.
  sportType?: VirtualSportType;
  // Specific betting week (absolute round) — VR line feed only.
  round?: number;
  page?: number;
  pageSize?: number;
  minOffset?: number;
  // Opt-in "N odds" badge count — only the Zero VR bülten (lineQuery) asks.
  // In the query key, so a flagged list never shares a cache entry with the
  // classic virtual pages' unflagged one.
  withOddsCount?: boolean;
  enabled?: boolean;
  refetchInterval?: number;
  /**
   * Restrict which query-state changes re-render the subscriber — the VR live
   * board polls every 3s and passes ["data", ...] so isFetching flips stop
   * re-rendering the whole tree. Omitted = notify on everything, as before.
   */
  notifyOnChangeProps?: ("data" | "isLoading" | "isPending" | "error")[];
}

// Dedicated VR event-list query — decoupled from the generic
// `useSportEventsNewList` so the shared hook carries no VR branch.
export const useVirtualSportEvents = (options: Options) => {
  const locale = useLocale();
  const sportType = options.sportType ?? DEFAULT_VIRTUAL_SPORT_TYPE;

  return useQuery<SportEvent[]>({
    queryKey: [
      VirtualSportQueryKeys.List,
      sportType,
      locale,
      options.feedType,
      options.round,
      options.withOddsCount,
    ],
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
    notifyOnChangeProps: options.notifyOnChangeProps,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      getVirtualSportEvents({
        signal,
        params: {
          lang: locale,
          feedType: options.feedType,
          page: options.page ?? 1,
          pageSize: options.pageSize,
          minOffset: options.minOffset,
          round: options.round,
          sportType,
          withOddsCount: options.withOddsCount,
        },
      }),
  });
};
